import React from "react";
import { ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { media } from "../mock";

const stats = [
  { value: "25+", label: "Projects Completed" },
  { value: "100+", label: "Project Involvement" },
  { value: "100%", label: "Safety Record" },
  { value: "DMV", label: "+ Nationwide" },
];

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={media.heroBg}
          alt="Robo Demo Co. excavator demolishing a multi-story concrete structure"
          className="w-full h-full object-cover slow-zoom"
          style={{ objectPosition: "center 68%" }}
        />
        {/* Left-weighted overlay keeps headline readable while showing the machine on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
        <div className="absolute inset-0 tex-grid opacity-30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-16">
        <div className="max-w-3xl">
          <div className="rise-in inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-4 py-2 mb-8">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Service-Disabled Veteran-Owned
            </span>
          </div>

          <h1
            className="rise-in font-display text-6xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] text-white"
            style={{ animationDelay: "0.05s" }}
          >
            We Tear It
            <br />
            <span className="text-primary">Down Right.</span>
          </h1>

          <p
            className="rise-in mt-8 text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed"
            style={{ animationDelay: "0.15s" }}
          >
            Precision demolition and site work built on military discipline.
            Residential, commercial, and complex industrial projects executed
            safely across the DMV area and nationwide.
          </p>

          <div
            className="rise-in mt-10 flex flex-col sm:flex-row gap-4"
            style={{ animationDelay: "0.25s" }}
          >
            <Button
              data-testid="hero-get-quote-btn"
              size="lg"
              onClick={() => navigate("/quote")}
              className="rounded-none font-bold uppercase tracking-widest px-8 py-6 text-base group"
            >
              Get a Free Quote
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-view-services-btn"
              size="lg"
              variant="outline"
              onClick={() => navigate("/services")}
              className="rounded-none font-bold uppercase tracking-widest px-8 py-6 text-base border-white/30 bg-transparent text-white hover:bg-white hover:text-background"
            >
              View Services
            </Button>
          </div>

          <div
            className="rise-in mt-8 flex items-center gap-2 text-sm text-gray-400"
            style={{ animationDelay: "0.3s" }}
          >
            <MapPin className="h-4 w-4 text-primary" />
            Primary service area: Washington DC, Maryland &amp; Virginia
          </div>
        </div>
      </div>

      {/* Stats band */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
            {stats.map((s) => (
              <div key={s.label} className="py-6 px-4 text-center md:text-left">
                <div className="font-display text-4xl md:text-5xl font-black text-white tracking-tight">
                  {s.value}
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
